import {defineStore} from "pinia";
import axios from "axios";
import {handleError, useInitializeStore} from "./storeHelper";
import {BTableOptions, serializeSortBy} from "@/components/BTable/BTable.types";
import {computed, reactive, ref} from "vue";
import {
    BFieldSelection,
    BFilterableField,
    BFilterOptions,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import type {UserField, UserInformation, GenerateApiKeyResponse} from "@/types";
import {serializeFilters} from "@/components/BTable/HelperFunctions";
import {PagedList} from "@/audit.types";
import {useGlobalMessagesStore} from "./globalMessagesStore";
import {useRoleStore} from "./roleStore";

export const useUserStore = defineStore("userStore", () => {
    const globalMessages = useGlobalMessagesStore();
    const apiUrl = "api/v1/user/";
    const loading = ref<boolean>(false);
    const items = ref<UserInformation[]>();
    const userFields = ref<UserField[]>();
    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                isDescending: false,
                key: "userName",
            },
        ],
        filterBy: [],
        pagingMode: "withTotal",
    } as BTableOptions);

    const roles = ref<BFieldSelection[]>();
    const filterableFields = ref<BFilterableField[]>();

    useInitializeStore(async () => {
        const roleStore = useRoleStore();
        await roleStore.list();

        if (roleStore.items == null) {
            return [] as BFieldSelection[];
        }

        roles.value = roleStore.items.map(
            (it) =>
                ({
                    text: it.roleName,
                    value: it.roleId,
                } as BFieldSelection)
        );

        filterableFields.value = reactive<BFilterableField[]>([
            {
                property: "userName",
                label: "Username",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "firstName",
                label: "First Name",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "lastName",
                label: "Last Name",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "organization",
                label: "Organization",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "email",
                label: "Email",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "roleId",
                label: "Role",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: roles.value,
            },
            {
                property: "isEnabled",
                label: "Enabled",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: [
                    {
                        text: "Enabled",
                        value: "true",
                    },
                    {
                        text: "Disabled",
                        value: "false",
                    },
                ],
            },
            {
                property: "custom1",
                label: "Custom1",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "custom2",
                label: "Custom2",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "custom3",
                label: "Custom3",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "apiKey",
                label: "API Key",
                operations: textOperations,
                editorComponent: "text-editor",
            },
        ]);
    });

    const exportFile = async (fileType: string) => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get(`${apiUrl}export`, {
                responseType: "blob",
                params: {
                    fileType,
                    sort: sortParam,
                    filter: filterParam,
                },
            });
           
            const blob = new Blob([response.data], {type: response.headers["content-type"]});

            return blob;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const list = async () => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<UserInformation>>(apiUrl, {
                params: {
                    limit: tableOptions.value.itemsPerPage,
                    offset: tableOptions.value.itemsPerPage * (tableOptions.value.page - 1),
                    sort: sortParam,
                    filter: filterParam,
                },
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };
    const listUserFields = async () => {
        try {
            loading.value = true;
            const response = await axios.get<UserField[]>(`${apiUrl}user-fields`);
            userFields.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const generateApiKey = async (userId: number) => {
        try {
            loading.value = true;
            const response = await axios.get<GenerateApiKeyResponse>(
                `${apiUrl}${userId}/generate-api-key/um_edit_apiKey`
            );
            return response.data.apiKey;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const create = async (user: UserInformation): Promise<void> => {
        try {
            loading.value = true;
            await axios.put(`${apiUrl}`, user);
            globalMessages.push({message: "User created successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const update = async (id, user: UserInformation): Promise<void> => {
        try {
            loading.value = true;
            await axios.post(`${apiUrl}${id}`, user);
            globalMessages.push({message: "User updated successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const deleteItem = async (userId: number): Promise<void> => {
        try {
            loading.value = true;
            await axios.delete(`${apiUrl}${userId}`);
            globalMessages.push({message: "User deleted successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getById = async (userId: number): Promise<UserInformation> => {
        try {
            loading.value = true;
            const response = await axios.get<UserInformation>(`${apiUrl}${userId}`);
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const validateUsername = async (userId: number, username: string): Promise<boolean> => {
        try {
            loading.value = true;
            var response = await axios.post<boolean>(`${apiUrl}actions/validate-username`, {userId, username});

            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const updateSettings = async (settingsFields: UserField[]) => {
        try {
            loading.value = true;
            await axios.post(`${apiUrl}user-fields`, settingsFields);
            globalMessages.push({message: "Settings updated successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };
    return {
        list,
        listUserFields,
        generateApiKey,
        getById,
        deleteItem,
        updateSettings,
        create,
        update,
        validateUsername,
        exportFile,
        items,
        userFields,
        filterableFields,
        filterOptions,
        tableOptions,
        roles,
        loading,
    };
});
